import { useCallback, useEffect, useMemo, useState } from "react"
import { Product } from "@/shared/types/product"
import { LISTINGS_ITEMS_PER_PAGE } from "@/features/listings/constants"

interface UseListingsPaginationResult {
  searchQuery: string
  setSearchQuery: (value: string) => void
  page: number
  setPage: (value: number) => void
  resetPage: () => void
  pageCount: number
  filteredProducts: Product[]
  pagedProducts: Product[]
  itemsPerPage: number
}

const useListingsPagination = (
  products: Product[],
  itemsPerPage = LISTINGS_ITEMS_PER_PAGE
): UseListingsPaginationResult => {
  const [searchQuery, setSearchQueryState] = useState("")
  const [page, setPageState] = useState(1)

  const filteredProducts = useMemo(
    () => products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase())),
    [products, searchQuery]
  )

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage)),
    [filteredProducts, itemsPerPage]
  )

  useEffect(() => {
    setPageState(prev => Math.min(Math.max(prev, 1), pageCount))
  }, [pageCount])

  const pagedProducts = useMemo(() => {
    const start = (page - 1) * itemsPerPage
    return filteredProducts.slice(start, start + itemsPerPage)
  }, [filteredProducts, page, itemsPerPage])

  const setSearchQuery = useCallback((value: string) => {
    setSearchQueryState(value)
    setPageState(1)
  }, [])

  const setPage = useCallback((value: number) => {
    setPageState(value)
  }, [])

  const resetPage = useCallback(() => {
    setPageState(1)
  }, [])

  return {
    searchQuery,
    setSearchQuery,
    page,
    setPage,
    resetPage,
    pageCount,
    filteredProducts,
    pagedProducts,
    itemsPerPage
  }
}

export { useListingsPagination }
